import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, Github, Calendar, Tag } from 'lucide-react';
import { portfolioAPI } from '../../services/api';

const ProjectDetailContainer = styled.div`
  padding: 120px 0 80px;
  min-height: 100vh;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  margin-bottom: 2rem;
  border-radius: 6px;
  transition: ${props => props.theme.transitions.fast};
  
  &:hover {
    background: rgba(0, 123, 255, 0.1);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const ProjectHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const ProjectTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.secondary};
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.dark};
  margin-bottom: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const ProjectDescription = styled(motion.p)`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
`;

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await portfolioAPI.getProject(id);
        setProject(response.data.data);
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <ProjectDetailContainer>
        <Content>
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <p>Loading project details...</p>
          </div>
        </Content>
      </ProjectDetailContainer>
    );
  }

  if (!project) {
    return (
      <ProjectDetailContainer>
        <Content>
          <BackButton to="/projects">
            <ArrowLeft />
            Back to Projects
          </BackButton>
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <p>Project not found.</p>
          </div>
        </Content>
      </ProjectDetailContainer>
    );
  }

  return (
    <ProjectDetailContainer>
      <Content>
        <BackButton to="/projects">
          <ArrowLeft />
          Back to Projects
        </BackButton>

        <ProjectHeader>
          <ProjectTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {project.title}
          </ProjectTitle>
          
          <ProjectDescription
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {project.description}
          </ProjectDescription>
        </ProjectHeader>

        {/* Project detail content will be expanded here */}
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <p>Project detail content coming soon...</p>
        </div>
      </Content>
    </ProjectDetailContainer>
  );
};

export default ProjectDetail;